import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { StatusLozengeButton } from '../../../../../../../common/ui/status-lozenge-button';
import { enrichExcerpt } from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphCase_AtlassianSearchPage$key } from './__generated__/searchGraphCase_AtlassianSearchPage.graphql';
import { messages } from './messages';

const styles = cssMap({
	caseContainer: {
		width: '100%',
	},
	resultWrapper: {
		flex: '1',
		minWidth: '0px',
	},
	content: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

export const SearchGraphCase = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphCase_AtlassianSearchPage$key>): React.JSX.Element => {
	const { formatMessage, formatDate } = useIntl();

	const data = useFragment(
		graphql`
			fragment searchGraphCase_AtlassianSearchPage on SearchResultGraphDocument {
				id
				url
				title
				lastModifiedDate
				excerpt
				entity {
					... on ExternalCase {
						displayNumber
						status
						closedAt
						lastUpdated
						owners {
							thirdPartyUser {
								name
							}
							user {
								name
							}
						}
						createdBy {
							thirdPartyUser {
								name
							}
						}
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalCustomerOrg {
								id
								displayName
							}
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { id, url, title, lastModifiedDate, excerpt, entity } = data;

	const ownerName =
		entity?.owners?.[0]?.thirdPartyUser?.name ??
		entity?.owners?.[0]?.user?.name ??
		entity?.createdBy?.thirdPartyUser?.name;

	const lastModifiedComponent = (
		<LastModified lastModified={entity?.lastUpdated ?? lastModifiedDate} />
	);

	const description = <DescriptionBody description={enrichExcerpt(excerpt)} />;

	const statusText = entity?.status ?? '';

	const closedAtValue = entity?.closedAt ? new Date(entity.closedAt) : null;
	const formattedClosedAt = closedAtValue
		? formatDate(closedAtValue, {
				month: 'long',
				day: 'numeric',
				year: 'numeric',
			})
		: '';
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const closedAtMessage =
		closedAtValue && closedAtValue >= today ? messages.closesOn : messages.closedOn;

	const closedAtPopupContent = closedAtValue ? (
		<Box xcss={styles.content}>
			<FormattedMessage {...closedAtMessage} values={{ date: formattedClosedAt }} />
		</Box>
	) : undefined;

	const containerName = entity?.container?.displayName;

	const headerMetadataParts = [
		formatMessage(messages.caseLabel),
		containerName,
		['owner', ownerName],
		['last-modified', lastModifiedComponent],
	];

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const footerComponent = (
		<MentionedByLinkedResults
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<Flex gap="space.100" alignItems="start" role="listitem" xcss={styles.caseContainer}>
			<Box xcss={styles.resultWrapper}>
				<SearchResult
					id={id}
					product={product}
					productSelected={!!selectedProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					icon={icon}
					description={description}
					headerMetadataParts={headerMetadataParts}
					title={
						entity?.displayNumber ? (
							<>
								<Box as="span" color="color.link">
									{entity.displayNumber}
								</Box>{' '}
								{enrichExcerpt(title)}
							</>
						) : (
							enrichExcerpt(title)
						)
					}
					role="none"
					url={url}
					isThirdParty
					cloudId={cloudId}
					primaryProduct={primaryProduct}
					position={position}
					partial={data}
					appMode={appMode}
					footer={footerComponent}
					hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
					mainContentWidth={mainContentWidth}
				/>
			</Box>
			{statusText && (
				<StatusLozengeButton statusText={statusText} popupContent={closedAtPopupContent} />
			)}
		</Flex>
	);
};
